import React, { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";

const LICENSE_TYPES = [
    { value: "new", label: "Nouvelle licence" },
    { value: "renewal", label: "Renouvellement" },
    { value: "upgrade", label: "Mise à niveau" },
    { value: "additional", label: "Licence supplémentaire" },
];

export function LicenseRequestForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("medium");
    const [softwareName, setSoftwareName] = useState("");
    const [softwareVersion, setSoftwareVersion] = useState("");
    const [licenseType, setLicenseType] = useState("");
    const [licenseCount, setLicenseCount] = useState(1);
    const [justification, setJustification] = useState("");
    const [licenseBeneficiaries, setLicenseBeneficiaries] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        const typeLabel = LICENSE_TYPES.find((t) => t.value === licenseType)?.label || licenseType;
        onSubmit({
            title: baseForm.title || `Demande de licence - ${softwareName}`,
            description: baseForm.description,
            priority: baseForm.priority,
            dynamic_fields: {
                software_name: softwareName,
                software_version: softwareVersion || null,
                license_type: typeLabel,
                license_count: licenseCount,
                justification: justification,
                license_beneficiaries: licenseBeneficiaries || null,
            },
        });
    };

    return (
        <form onSubmit={handleSubmit} data-testid="license-request-form">
            <BaseFormFields
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                {...baseForm}
                onTitleChange={onTitleChange}
                allowedPriorities={allowedPriorities}
                titlePlaceholder="Ex: Licence AutoCAD 2025 pour le bureau d'études"
                descriptionPlaceholder="Contexte de la demande de licence..."
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <Label className="form-label">Logiciel concerné</Label>
                        <Input
                            value={softwareName}
                            onChange={(e) => setSoftwareName(e.target.value)}
                            placeholder="Ex: AutoCAD, Adobe Creative Suite, Relux..."
                            data-testid="software-name"
                        />
                    </div>

                    <div className="form-group">
                        <Label className="form-label">Version</Label>
                        <Input
                            value={softwareVersion}
                            onChange={(e) => setSoftwareVersion(e.target.value)}
                            placeholder="Ex: 2025, v3.2..."
                            data-testid="software-version"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <Label className="form-label">Type de licence</Label>
                        <Select value={licenseType} onValueChange={setLicenseType}>
                            <SelectTrigger data-testid="license-type">
                                <SelectValue placeholder="Sélectionner..." />
                            </SelectTrigger>
                            <SelectContent>
                                {LICENSE_TYPES.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="form-group">
                        <Label className="form-label">Nombre de licences</Label>
                        <Input
                            type="number"
                            value={licenseCount}
                            onChange={(e) => setLicenseCount(Math.max(1, parseInt(e.target.value) || 1))}
                            min={1}
                            data-testid="license-count"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <Label className="form-label">Justification</Label>
                    <Textarea
                        value={justification}
                        onChange={(e) => setJustification(e.target.value)}
                        placeholder="Pourquoi cette licence est-elle nécessaire ?"
                        rows={3}
                        data-testid="justification"
                    />
                </div>

                <div className="form-group">
                    <Label className="form-label">Bénéficiaire(s)</Label>
                    <Textarea
                        value={licenseBeneficiaries}
                        onChange={(e) => setLicenseBeneficiaries(e.target.value)}
                        placeholder="Si différent du demandeur, indiquez les utilisateurs concernés"
                        rows={2}
                        data-testid="license-beneficiaries"
                    />
                </div>
            </BaseFormFields>

            <div className="mt-6 flex justify-end">
                <Button type="submit" disabled={!baseForm.title || submitting} data-testid="submit-ticket">
                    {submitting ? "Création..." : "Soumettre le ticket"}
                </Button>
            </div>
        </form>
    );
}
